import Badge from "antd/lib/badge";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { useActiveClassroom, useAuthenticatedUserClassrooms } from "@sol/classrooms";
import { IClassroomItem } from "@sol/sdk";
import { ChevronDown } from "@sol/uikit/core/icons/Arrows/ChevronDown";
import { Search01 } from "@sol/uikit/core/icons/General/Search01";
import { Tooltip } from "@sol/uikit/data-display/Tooltip/Tooltip";
import { Select } from "@sol/uikit/data-entry/Select/Select";

import styles from "./ActiveClassroomSelect.module.css";
import useSearch from "../../../hooks/useSearch";
import { prettyTitle } from "../../../utils/prettyTitle";

const TruncatedDisplayLabel = ({ text }: { text: string }) => {
    return (
        <Tooltip title={text}>
            <span className={styles.displayLabel}>{text}</span>
        </Tooltip>
    );
};

const normalizeSearchText = (title: string) => {
    const pretty = prettyTitle(title);

    return [title, title.replaceAll("_", " "), pretty, pretty.toLowerCase()].join(" ");
};

export const ActiveClassroomSelect = () => {
    const { setActiveClassroom, activeClassroom } = useActiveClassroom();
    const { classrooms } = useAuthenticatedUserClassrooms();
    const [t] = useTranslation();
    const [isOpen, setIsOpen] = useState(false);

    const indexedClassrooms = useMemo(
        () =>
            (classrooms ?? []).map(classroom => ({
                classroom,
                searchText: normalizeSearchText(classroom.title),
            })),
        [classrooms],
    );

    const {
        results: searchResults,
        search,
        setSearch,
    } = useSearch(indexedClassrooms, {
        keys: ["searchText"],
        threshold: 0.3,
        ignoreLocation: true,
        minMatchCharLength: 2,
    });

    const resetSearch = useCallback(() => setSearch(""), [setSearch]);

    const handleChange = useCallback(
        (value: string) => {
            setActiveClassroom(value);
            resetSearch();
        },
        [resetSearch, setActiveClassroom],
    );

    const handleOpenChange = useCallback(
        (visible: boolean) => {
            setIsOpen(visible);
            resetSearch();
        },
        [resetSearch],
    );

    const renderOptionLabel = useCallback(
        (classroom: IClassroomItem) => (
            <>
                <Badge
                    status={classroom.active ? "processing" : "default"}
                    className="mr-2"
                    text={prettyTitle(classroom.title)}
                />
                <span className="sr-only">
                    {prettyTitle(classroom.title)}
                    {t(classroom.active ? "page.classroomDashboard.in-progress" : "page.classroomDashboard.finished")}
                </span>
            </>
        ),
        [t],
    );

    const options = useMemo(() => {
        const sortedSearchResults = search
            ? searchResults
            : [...searchResults].sort(
                  (a, b) =>
                      Number(b.classroom.active) - Number(a.classroom.active) ||
                      a.classroom.title.localeCompare(b.classroom.title),
              );

        return sortedSearchResults.map(({ classroom }) => {
            const title = prettyTitle(classroom.title);

            return {
                label: renderOptionLabel(classroom),
                displayLabel: <TruncatedDisplayLabel text={title} />,
                value: classroom["@id"],
                title: "",
            };
        });
    }, [search, searchResults, renderOptionLabel]);

    return (
        <Select
            data-cy="active-classroom-select"
            aria-label="Select active classroom"
            value={activeClassroom?.["@id"]}
            onChange={handleChange}
            onSearch={setSearch}
            onOpenChange={handleOpenChange}
            searchValue={search}
            showSearch
            filterOption={false}
            variant="borderless"
            options={options}
            optionLabelProp="displayLabel"
            popupMatchSelectWidth={false}
            className={styles.select}
            suffixIcon={
                isOpen ? (
                    <Search01 size={14} className="text-primary" />
                ) : (
                    <ChevronDown size={16} className="text-primary" />
                )
            }
        />
    );
};
